import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const DieMaster = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    die_no: '',
    cust_code: '',
    part_name: '',
    part_no: '',
    steel_code: '',
    ht_code: '',
    hardness_range_low: '',
    hardness_range_high: '',
    net_weight: '',
    cut_weight: '',
    status: 'A'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [steels, setSteels] = useState([]);
  const [htCodes, setHtCodes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
    fetchCustomers();
    fetchSteels();
    fetchHtCodes();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/masters/dies');
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching die records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/masters/customers');
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSteels = async () => {
    try {
      const response = await api.get('/masters/steels');
      setSteels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching steels:', error);
    }
  };

  const fetchHtCodes = async () => {
    try {
      const response = await api.get('/masters/ht-codes');
      setHtCodes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching HT codes:', error);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      die_no: '',
      cust_code: '',
      part_name: '',
      part_no: '',
      steel_code: '',
      ht_code: '',
      hardness_range_low: '',
      hardness_range_high: '',
      net_weight: '',
      cut_weight: '',
      status: 'A'
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/masters/dies/${currentRecord.die_no}`, currentRecord);
      } else {
        await api.post('/masters/dies', currentRecord);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving die record:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'A': return 'success';
      case 'I': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'A': return 'Active';
      case 'I': return 'Inactive';
      default: return status;
    }
  };

  const filteredRecords = records.filter(record =>
    record.die_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.part_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.part_no?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EngineeringIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Die Master
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Manage die specifications, hardness requirements, and customer assignments
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Dies
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Dies
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.status === 'A').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Customers
              </Typography>
              <Typography variant="h4" color="info.main">
                {customers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                HT Processes
              </Typography>
              <Typography variant="h4" color="warning.main">
                {htCodes.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search dies..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
        >
          Export
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Die No.</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Part Name</strong></TableCell>
                <TableCell><strong>Part No.</strong></TableCell>
                <TableCell><strong>Steel Code</strong></TableCell>
                <TableCell><strong>HT Code</strong></TableCell>
                <TableCell><strong>Hardness Range</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Alert severity="info">
                      No die master records found. This data will be available once migration is completed.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => (
                  <TableRow key={record.die_no} hover>
                    <TableCell>{record.die_no}</TableCell>
                    <TableCell>
                      {customers.find(c => c.cust_code === record.cust_code)?.cust_name || record.cust_code}
                    </TableCell>
                    <TableCell>{record.part_name}</TableCell>
                    <TableCell>{record.part_no}</TableCell>
                    <TableCell>{record.steel_code}</TableCell>
                    <TableCell>{record.ht_code}</TableCell>
                    <TableCell>
                      {record.hardness_range_low && record.hardness_range_high
                        ? `${record.hardness_range_low} - ${record.hardness_range_high} HRC`
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(record.status)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(record)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Die Master' : 'Add New Die'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Die Number"
                value={currentRecord.die_no}
                onChange={(e) => setCurrentRecord({...currentRecord, die_no: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={currentRecord.cust_code}
                  label="Customer"
                  onChange={(e) => setCurrentRecord({...currentRecord, cust_code: e.target.value})}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.cust_code} value={customer.cust_code}>
                      {customer.cust_code} - {customer.cust_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Name"
                value={currentRecord.part_name}
                onChange={(e) => setCurrentRecord({...currentRecord, part_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Number"
                value={currentRecord.part_no}
                onChange={(e) => setCurrentRecord({...currentRecord, part_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Steel Code</InputLabel>
                <Select
                  value={currentRecord.steel_code}
                  label="Steel Code"
                  onChange={(e) => setCurrentRecord({...currentRecord, steel_code: e.target.value})}
                >
                  {steels.map((steel) => (
                    <MenuItem key={steel.steel_code} value={steel.steel_code}>
                      {steel.steel_code} - {steel.specification}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>HT Code</InputLabel>
                <Select
                  value={currentRecord.ht_code}
                  label="HT Code"
                  onChange={(e) => setCurrentRecord({...currentRecord, ht_code: e.target.value})}
                >
                  {htCodes.map((ht) => (
                    <MenuItem key={ht.ht_code} value={ht.ht_code}>
                      {ht.ht_code} - {ht.ht_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hardness Low (HRC)"
                type="number"
                value={currentRecord.hardness_range_low}
                onChange={(e) => setCurrentRecord({...currentRecord, hardness_range_low: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hardness High (HRC)"
                type="number"
                value={currentRecord.hardness_range_high}
                onChange={(e) => setCurrentRecord({...currentRecord, hardness_range_high: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Net Weight (Kg)"
                type="number"
                value={currentRecord.net_weight}
                onChange={(e) => setCurrentRecord({...currentRecord, net_weight: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cut Weight (Kg)"
                type="number"
                value={currentRecord.cut_weight}
                onChange={(e) => setCurrentRecord({...currentRecord, cut_weight: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={currentRecord.status}
                  label="Status"
                  onChange={(e) => setCurrentRecord({...currentRecord, status: e.target.value})}
                >
                  <MenuItem value="A">Active</MenuItem>
                  <MenuItem value="I">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DieMaster;